/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { createPrebuiltEntityStore } from 'src/luis/core/stores/prebuiltEntityStore';
import { EntitySwagger } from 'src/luis/projects/entities/types/swagger/EntitySwagger';
import { PrebuiltEntitySwagger } from 'src/luis/projects/entities/types/swagger/PrebuiltEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { PrebuiltEntityStore } from 'src/luis/types/stores/entities/PrebuiltEntityMetadataStore';

/**
 * Represents a service to perform CRUD operations for prebuilt entities / domains entities.
 */
export const getPrebuiltEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Gets the prebuilt entities.
     */
    const getPrebuiltEntities = async (): Promise<PrebuiltEntityStore[]> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('listPrebuilts')
            .build();

        const { data: models } = await httpService.get<PrebuiltEntitySwagger[]>(url, apiOptions);

        return models.map(createPrebuiltEntityStore);
    };

    /**
     * Add the prebuilt entities to the current app.
     *
     * @param entityNames The names of the prebuilt entities to be added.
     */
    const addPrebuiltEntities = async (entityNames: string[]): Promise<EntityStore[]> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('prebuilts')
            .build();

        const { data: entities } = await httpService.post<EntitySwagger[]>(url, entityNames, apiOptions);

        return entities.map(entitySwaggerToEntityStore);
    };

    /**
     * Add the prebuilt domain entities to the current app.
     *
     * @param entityNames The names of the prebuilt entities to be added.
     */
    const addPrebuiltDomainEntity = async (prebuiltDomain: DomainModel): Promise<string> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('customPrebuiltEntities')
            .build();

        const { data: id } = await httpService.post<string>(url, prebuiltDomain, apiOptions);

        return id;
    };

    /**
     * Deletes the given prebuilt entity.
     *
     * @param id The id of the prebuilt entity to delete.
     */
    const deleteEntity = async (id: LuisId = ''): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('entities', id)
            .build();

        await httpService.delete(url, apiOptions);
    };

    return { getPrebuiltEntities, addPrebuiltEntities, addPrebuiltDomainEntity, deleteEntity };
});
